import { FilePlus2, Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthStore } from "@/features/auth/store/auth-store";
import {
  LETTER_RECIPIENT_OPTIONS,
  cloneTemplate,
  deleteLetterTemplate,
  hideTemplate,
  listLetterTemplates,
  restoreTemplate,
} from "@/features/letters/api";
import type { LetterTemplate } from "@/features/letters/types";
import { LetterPreviewDialog } from "@/features/settings/components/tabs/letter-preview-dialog";
import { LetterTemplateCard } from "@/features/settings/components/tabs/letter-template-card";
import { LetterTemplateDialog } from "@/features/settings/components/tabs/letter-template-dialog";
import { LetterheadSection } from "@/features/settings/components/tabs/letterhead-section";
import { Segmented } from "@/features/settings/components/segmented";
import { PageHeader } from "@/components/common/page-header";
import { TabShell } from "@/features/settings/components/tab-shell";
import { useFirmSettings } from "@/features/settings/hooks/use-firm-settings";
import { ApiError } from "@/lib/api";

type SourceFilter = "all" | "firm" | "system" | "hidden";

/** Quick-reference merge-field tokens (click a chip to copy it). */
const MERGE_FIELD_TOKENS = [
  "{client_name}",
  "{taxpayer_name}",
  "{case_number}",
  "{tax_year}",
  "{practitioner_name}",
  "{firm_name}",
  "{today}",
];

export function LettersTab() {
  const [list, setList] = useState<LetterTemplate[] | null>(null);
  const [error, setError] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LetterTemplate | undefined>();
  const [source, setSource] = useState<SourceFilter>("all");
  const [recipient, setRecipient] = useState("ANY");
  const [previewTemplate, setPreviewTemplate] = useState<LetterTemplate | undefined>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const isAdmin = useAuthStore((s) => s.user?.role === "country_director");
  const { settings, setSettings } = useFirmSettings();

  function reload() {
    listLetterTemplates(true)
      .then(setList)
      .catch(() => setError(true));
  }

  useEffect(() => {
    let active = true;
    listLetterTemplates(true)
      .then((t) => active && setList(t))
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  function upsert(saved: LetterTemplate) {
    setList((prev) => {
      if (!prev) return [saved];
      const idx = prev.findIndex((t) => t.id === saved.id);
      if (idx === -1) return [...prev, saved];
      const next = [...prev];
      next[idx] = saved;
      return next;
    });
  }

  async function handleDelete(t: LetterTemplate) {
    try {
      await deleteLetterTemplate(t.id);
      setList((prev) => prev?.filter((x) => x.id !== t.id) ?? null);
      toast.success("Template deleted");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete the template.");
    }
  }

  async function handleClone(t: LetterTemplate) {
    try {
      const copy = await cloneTemplate(t.id);
      upsert(copy);
      toast.success("Template cloned to your firm");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't clone the template.");
    }
  }

  async function handleToggleHide(t: LetterTemplate) {
    try {
      if (t.isHidden) {
        await restoreTemplate(t.id);
        toast.success("Template restored");
      } else {
        await hideTemplate(t.id);
        toast.success("Template hidden");
      }
      reload();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update the template.");
    }
  }

  function handleEdit(t: LetterTemplate) {
    setEditing(t);
    setDialogOpen(true);
  }

  function handlePreview(t: LetterTemplate) {
    setPreviewTemplate(t);
    setPreviewOpen(true);
  }

  function handleCopyToken(token: string) {
    navigator.clipboard?.writeText(token).then(
      () => toast.success(`Copied ${token}`),
      () => toast.error("Couldn't copy to clipboard."),
    );
  }

  const counts = useMemo(() => {
    const items = list ?? [];
    return {
      all: items.length,
      firm: items.filter((t) => !t.isSystem).length,
      system: items.filter((t) => t.isSystem && !t.isHidden).length,
      hidden: items.filter((t) => t.isHidden).length,
      systemTotal: items.filter((t) => t.isSystem).length,
    };
  }, [list]);

  const filtered = useMemo(() => {
    let items = list ?? [];
    if (source === "firm") items = items.filter((t) => !t.isSystem);
    else if (source === "system") items = items.filter((t) => t.isSystem && !t.isHidden);
    else if (source === "hidden") items = items.filter((t) => t.isHidden);
    if (recipient !== "ANY") items = items.filter((t) => t.recipient === recipient);
    return items;
  }, [list, source, recipient]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Letter templates"
        subtitle={
          <>
            Reusable letters on your firm&apos;s letterhead.{" "}
            <strong className="text-foreground font-semibold">
              {counts.systemTotal} system templates
            </strong>{" "}
            are shared and read-only — clone one to make it yours, or start from blank.
          </>
        }
        actions={
          isAdmin && list ? (
            <Button
              type="button"
              variant="cta"
              className="gap-1.5"
              onClick={() => {
                setEditing(undefined);
                setDialogOpen(true);
              }}
            >
              <Plus className="size-4" />
              New template
            </Button>
          ) : undefined
        }
      />
      <TabShell loading={!list && !error} error={error}>
        {list && (
          <div className="flex flex-col gap-6">
            {/* Filters */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Segmented<SourceFilter>
                label="Template source"
                value={source}
                onChange={setSource}
                options={[
                  { value: "all", label: `All · ${counts.all}` },
                  { value: "firm", label: `Your firm · ${counts.firm}` },
                  { value: "system", label: `System · ${counts.system}` },
                  { value: "hidden", label: `Hidden · ${counts.hidden}` },
                ]}
              />
              <Select value={recipient} onValueChange={setRecipient}>
                <SelectTrigger className="h-9" aria-label="Filter by recipient">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ANY">Any recipient</SelectItem>
                  {LETTER_RECIPIENT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Gallery */}
            {filtered.length === 0 && !(isAdmin && source !== "hidden") ? (
              <p className="text-muted-foreground text-sm">No letter templates match this filter.</p>
            ) : (
              <div className="grid gap-[18px] [grid-template-columns:repeat(auto-fit,minmax(min(300px,100%),1fr))]">
                {filtered.map((t) => (
                  <LetterTemplateCard
                    key={t.id}
                    template={t}
                    isAdmin={isAdmin}
                    onClone={handleClone}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleHide={handleToggleHide}
                    onPreview={handlePreview}
                  />
                ))}

                {isAdmin && source !== "hidden" && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(undefined);
                      setDialogOpen(true);
                    }}
                    className="border-border hover:border-info hover:bg-info-soft/40 flex min-h-[260px] flex-col items-center justify-center gap-3 rounded-xl border-[1.5px] border-dashed bg-card/50 p-7 text-center transition-colors"
                  >
                    <span className="bg-card text-info shadow-card flex size-14 items-center justify-center rounded-lg border">
                      <FilePlus2 className="size-6" strokeWidth={1.9} />
                    </span>
                    <span className="flex flex-col gap-1">
                      <span className="font-heading text-[17px] font-semibold tracking-tight">
                        Write your own letter
                      </span>
                      <span className="text-muted-foreground max-w-[260px] text-[13px]">
                        Start blank on your letterhead, drop in merge fields, and it joins this
                        gallery for the whole firm.
                      </span>
                    </span>
                    <span className="bg-info-soft text-info inline-flex h-9 items-center gap-1.5 rounded-sm px-3.5 text-[13px] font-semibold">
                      New template
                    </span>
                  </button>
                )}
              </div>
            )}

            {/* Merge-field quick reference */}
            <div className="bg-card flex flex-wrap items-center gap-x-2.5 gap-y-2 rounded-xl border px-4 py-3.5">
              <span className="text-muted-foreground mr-1.5 text-xs font-semibold tracking-[0.08em] uppercase">
                Merge fields
              </span>
              {MERGE_FIELD_TOKENS.map((token) => (
                <button
                  key={token}
                  type="button"
                  onClick={() => handleCopyToken(token)}
                  className="border-border bg-muted/50 text-muted-foreground hover:bg-info-soft hover:text-info h-[30px] rounded-sm border px-2.5 text-xs font-semibold transition-colors"
                >
                  {token}
                </button>
              ))}
            </div>

            <LetterTemplateDialog
              key={editing?.id ?? "new"}
              template={editing}
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              onSaved={upsert}
            />
            <LetterPreviewDialog
              template={previewTemplate}
              open={previewOpen}
              onOpenChange={setPreviewOpen}
            />
          </div>
        )}
      </TabShell>

      {settings && (
        <LetterheadSection settings={settings} setSettings={setSettings} isAdmin={isAdmin} />
      )}
    </div>
  );
}
